import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { rem, rgba } from 'polished';
import { animations, colors, mixins } from '../../core/styles/theme/default';
import { P1 } from '../../primitives';
import { ImageInterface } from '../../types';
import ImageWithCaption from '../Image';

const StyledTeaser = styled.div(
  ({ as }: { as: string }) => css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: stretch;
    border-radius: ${rem(2)};
    background-color: ${colors.blue800};

    ${mixins.responsiveStyles({
      padding: { zero: 16, medium: 32 },
    })}

    ${['a', 'button'].includes(as) &&
    css`
      border: none;
      cursor: pointer;
      transition: box-shadow ${animations.default};

      &:hover,
      &:focus {
        box-shadow: 0 ${rem(10)} ${rem(15)} ${rgba(colors.black, 0.3)},
          0 ${rem(4)} ${rem(6)} ${rgba(colors.grey900, 0.2)};
        outline: none;
      }

      .m-richtext & {
        text-decoration: none;
      }
    `}
  `,
);

const StyledLogo = styled.div`
  display: flex;
  align-items: center;
  padding: ${rem(16)};
  border-radius: ${rem(2)};
  background-color: ${colors.white};
  flex-grow: 1;

  ${mixins.responsiveStyles({
    'margin-bottom': { zero: 16, medium: 32 },
  })}

  figure {
    text-align: center;
    width: 100%;
    height: ${rem(140)};

    .gatsby-image-wrapper {
      height: inherit;
    }

    img {
      object-fit: contain !important;
    }
  }
`;

const StyledTitle = styled(P1)`
  text-align: center;
  font-weight: bold;
  color: ${colors.white};
`;

const logoSizes = {
  0: [335, 140],
  1280: [250, 104],
  1440: [302, 126],
};

const TeaserMeasure = ({ title, logo, onClick, href }: TeaserMeasureProps) => {
  const as = href ? 'a' : onClick ? 'button' : 'div';

  return (
    <StyledTeaser
      as={as}
      {...(onClick && { onClick: () => onClick() })}
      {...(href && { href })}
    >
      <StyledLogo>
        <ImageWithCaption
          data={{
            ...logo,
            imageSizes: logoSizes,
            imageFill: false,
            disableLink: true,
          }}
        />
      </StyledLogo>
      <StyledTitle>{title}</StyledTitle>
    </StyledTeaser>
  );
};

export type TeaserMeasureProps = {
  title: string;
  logo: ImageInterface;
  onClick?: () => void;
  href?: string;
};

export default TeaserMeasure;
